import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './PhotographerDashboard.css';

interface WeddingInfo {
    id: string;
    coupleName: string;
    weddingDate: string;
    storageLimit: number;
    storageUsage: number;
}

const PhotographerDashboard: React.FC = () => {
    const { user, accessToken } = useAuth();
    const history = useHistory();
    const [wedding, setWedding] = useState<WeddingInfo | null>(null);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        const fetchWeddingData = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/wedding/current`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                const data = await res.json();
                if (res.ok) setWedding(data.wedding);
            } catch (err) {
                console.error('Failed to fetch wedding data');
            }
        };
        fetchWeddingData();
    }, [accessToken]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        setStatus({ type: 'info', message: `Uploading ${files.length} photo(s)...` });

        const formData = new FormData();
        formData.append('weddingId', wedding?.id || '');

        // Handling single upload for now as per backend logic, 
        // but could be looped if backend is updated for multi-upload
        formData.append('image', files[0]);

        try {
            const res = await fetch(`${API_BASE_URL}/api/gallery/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${accessToken}` },
                body: formData
            });

            const data = await res.json();
            if (res.ok) {
                setStatus({ type: 'success', message: 'Photo uploaded successfully!' });
                // Refresh wedding data for storage usage
                const refreshRes = await fetch(`${API_BASE_URL}/api/wedding/current`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                const refreshData = await refreshRes.json();
                if (refreshRes.ok) setWedding(refreshData.wedding);
            } else {
                setStatus({ type: 'error', message: data.message || 'Upload failed' });
            }
        } catch (err) {
            setStatus({ type: 'error', message: 'Network error during upload' });
        } finally {
            setUploading(false);
        }
    };

    if (!wedding) return <div className="loading">Loading Wedding Details...</div>;

    const storageUsageMB = (wedding.storageUsage / (1024 * 1024)).toFixed(1);
    const storageLimitMB = wedding.storageLimit;
    const usagePercent = Math.min(100, ((wedding.storageUsage || 0) / (storageLimitMB * 1024 * 1024)) * 100);

    return (
        <div className="photographer-dashboard">
            <header className="dashboard-header">
                <div className="user-welcome">
                    <h1>Photographer Portal</h1>
                    <p>Capturing memories for <strong>{wedding.coupleName}</strong></p>
                </div>
            </header>

            <main className="dashboard-content">
                <div className="stats-grid">
                    <div className="stat-card storage">
                        <h3>Storage Usage</h3>
                        <div className="progress-container">
                            <div className="progress-bar" style={{ width: `${usagePercent}%`, background: usagePercent > 90 ? '#ff4d4f' : '#C5A059' }}></div>
                        </div>
                        <p>{storageUsageMB} MB / {storageLimitMB} MB Used</p>
                    </div>
                </div>

                <div className="upload-section card">
                    <div className="card-header-flex">
                        <h2>Upload Photos</h2>
                        <button className="view-gallery-link" onClick={() => history.push('/admin/gallery')}>
                            View Gallery 🖼️
                        </button>
                    </div>
                    <p className="hint">Share your professional shots directly to the guest gallery.</p>

                    <div className="upload-box">
                        <input
                            type="file"
                            id="photo-upload"
                            accept="image/*"
                            onChange={handleFileUpload}
                            disabled={uploading}
                            hidden
                        />
                        <label htmlFor="photo-upload" className={`upload-label ${uploading ? 'disabled' : ''}`}>
                            {uploading ? (
                                <span className="loader"></span>
                            ) : (
                                <>
                                    <span className="icon">📸</span>
                                    <span>Select Photo to Upload</span>
                                </>
                            )}
                        </label>
                    </div>

                    {status.message && (
                        <div className={`status-message ${status.type}`}>
                            {status.message}
                        </div>
                    )}
                </div>

                <div className="info-card card">
                    <h3>Quick Tips</h3>
                    <ul>
                        <li>Photos uploaded here are immediately visible to all Guests.</li>
                        <li>Ensure high quality, optimized JPG/PNG formats.</li>
                        <li>Monitor storage usage to avoid upload failures.</li>
                    </ul>
                </div>
            </main>
        </div>
    );
};

export default PhotographerDashboard;
